import { useEffect, useRef } from "react"

import { cn } from "@/lib/utils"

export interface WheelOption {
  label: string
  value: string
}

export interface WheelColumnConfig {
  key: string
  options: WheelOption[]
  /** Optional flex weight for this column's width. Defaults to 1. */
  flex?: number
}

interface WheelPickerProps {
  columns: WheelColumnConfig[]
  value: Record<string, string>
  onChange: (key: string, value: string) => void
  /** Number of rows visible (odd number keeps the selection centered). */
  visibleCount?: number
  itemHeight?: number
  className?: string
}

/**
 * iOS-style scroll wheel picker. Generic over any set of columns — used by the
 * date and time quiz steps, but reusable anywhere a snapping value wheel is needed.
 */
function WheelPicker({
  columns,
  value,
  onChange,
  visibleCount = 5,
  itemHeight = 40,
  className,
}: WheelPickerProps) {
  const padding = ((visibleCount - 1) / 2) * itemHeight

  return (
    <div
      data-slot="wheel-picker"
      className={cn("relative w-full select-none", className)}
      style={{ height: visibleCount * itemHeight }}
    >
      {/* Centered selection highlight spanning all columns */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 z-10 rounded-2xl border border-main-purple/70 bg-purple-light/5"
        style={{ height: itemHeight, top: padding }}
      />
      <div className="flex h-full items-stretch">
        {columns.map((column) => (
          <WheelColumn
            key={column.key}
            options={column.options}
            value={value[column.key]}
            onChange={(next) => onChange(column.key, next)}
            itemHeight={itemHeight}
            padding={padding}
            flex={column.flex ?? 1}
          />
        ))}
      </div>
    </div>
  )
}

interface WheelColumnProps {
  options: WheelOption[]
  value: string | undefined
  onChange: (value: string) => void
  itemHeight: number
  padding: number
  flex: number
}

function WheelColumn({ options, value, onChange, itemHeight, padding, flex }: WheelColumnProps) {
  const ref = useRef<HTMLDivElement>(null)
  const scrollTimeout = useRef<ReturnType<typeof setTimeout>>(undefined)
  const isUserScrolling = useRef(false)

  const selectedIndex = Math.max(
    0,
    options.findIndex((o) => o.value === value),
  )

  // Keep the wheel aligned to the selected value when it changes externally.
  useEffect(() => {
    const el = ref.current
    if (!el || isUserScrolling.current) return
    const target = selectedIndex * itemHeight
    if (Math.abs(el.scrollTop - target) > 1) {
      el.scrollTo({ top: target })
    }
  }, [selectedIndex, itemHeight])

  const handleScroll = () => {
    const el = ref.current
    if (!el) return
    isUserScrolling.current = true
    clearTimeout(scrollTimeout.current)
    scrollTimeout.current = setTimeout(() => {
      const index = Math.round(el.scrollTop / itemHeight)
      const clamped = Math.min(Math.max(index, 0), options.length - 1)
      el.scrollTo({ top: clamped * itemHeight, behavior: "smooth" })
      isUserScrolling.current = false
      const next = options[clamped]
      if (next && next.value !== value) onChange(next.value)
    }, 120)
  }

  return (
    <div
      ref={ref}
      onScroll={handleScroll}
      className="no-scrollbar snap-y snap-mandatory overflow-y-auto"
      style={{ flex, scrollPaddingBlock: padding }}
    >
      <div style={{ paddingTop: padding, paddingBottom: padding }}>
        {options.map((option, index) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={cn(
              "flex w-full snap-center items-center justify-center transition-colors",
              index === selectedIndex
                ? "text-lg font-semibold text-purple-light"
                : "text-base text-purple-light/30",
            )}
            style={{ height: itemHeight }}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )
}

export { WheelPicker }
